import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
  BOProduct,
  CmsComponent,
  CmsComponentData,
  CMSTabParagraphContainer,
} from '@boels/models';
import { BreakpointService, WindowRef } from '@boels/helpers';
import { ComponentDataService } from '@boels/shared-state';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { ProductAttributesComponent } from '../product-attributes-tab';
import { ProductBimTabComponent } from '../product-bim-tab';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { ProductAccTabComponent } from '../product-acc-tab/product-acc-tab.component';

@Component({
  selector: 'boels-tab-paragraph-container',
  templateUrl: './tab-paragraph-container.component.html',
  styleUrls: ['./tab-paragraph-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    CommonModule,
    ProductAttributesComponent,
    ProductAccTabComponent,
    ProductBimTabComponent,
    FontAwesomeModule,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['productDetails'],
    },
  ],
})
export class TabParagraphContainerComponent implements OnInit, OnDestroy {
  protected componentDataService = inject(ComponentDataService);
  protected winRef = inject(WindowRef);
  protected breakpointService = inject(BreakpointService);

  expandIcon = faChevronDown;
  collapseIcon = faChevronUp;
  activeTabNum = 0;
  tabTitleParams: (Observable<any> | null)[] = [];
  subscription: Subscription;
  components$: Observable<any[]>;
  @Input()
  public componentData: CmsComponentData<CMSTabParagraphContainer> & {
    product$: Observable<BOProduct>;
  };

  select(tabNum: number, event?: MouseEvent): void {
    this.breakpointService?.smAndBelow$.pipe(take(1)).subscribe((res) => {
      if (res) {
        this.activeTabNum = this.activeTabNum === tabNum ? -1 : tabNum;
        if (event && event?.currentTarget) {
          const target = event.currentTarget as HTMLElement;
          const parentNode = target.parentNode as HTMLElement;
          this.winRef.nativeWindow?.scrollTo(0, parentNode.offsetTop);
        }
      } else {
        this.activeTabNum = tabNum;
      }
    });
  }

  ngOnInit(): void {
    this.components$ = this.setComponentsData();

    this.activeTabNum =
      this.winRef.nativeWindow?.history?.state?.activeTab ?? this.activeTabNum;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  setComponentsData(): Observable<any[]> {
    return combineLatest([
      this.componentData.data$,
      this.componentData.product$,
    ]).pipe(
      switchMap(([data, product]) =>
        this.componentDataService
          .getCMSChildrenData(data, [
            ['fields', 'DEFAULT'],
            ['productCode', product.code],
          ])
          .pipe(
            map((tabsData) =>
              tabsData
                ?.filter((tab) => !!tab)
                .map((tab: CmsComponent) => {
                  if (!tab.flexType) {
                    tab = {
                      ...tab,
                      flexType: tab.typeCode,
                    };
                  }

                  return {
                    ...tab,
                    title: `productDetails.tabs.${tab.uid}`,
                  };
                }),
            ),
          ),
      ),
    );
  }
}
